import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useFormik } from 'formik';
import { IForgotPasswordCredentials } from '../../schemas/apis/interface';
import { useMutation } from '@tanstack/react-query';
import { forgotPasswordApi } from '../../utils/apis';
import { isAxiosError } from 'axios';
import { forgotPasswordSchema } from '../../schemas';

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
}) => {
  const { isPending, mutateAsync } = useMutation({
    mutationKey: ['forgot-password'],
    mutationFn: forgotPasswordApi,
    onSuccess: () => {
      resetForm();
      onClose();
      onSuccess('Reset token sent to your email address');
    },
    onError: err => {
      if (isAxiosError(err)) {
        if (err.message === 'Network Error') {
          onError('Network Error');
        } else if (err.response?.data) {
          const errorMessage = (err.response.data as { message: string })
            .message;
          onError(errorMessage);
        }
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: {
      email: '',
    } as IForgotPasswordCredentials,
    validationSchema: forgotPasswordSchema,
    onSubmit: values => {
      mutateAsync(values);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Reset Password</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="body2" mb={3}>
            Please enter your email address. You will receive a link to create a
            new password via email.
          </Typography>

          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.email && Boolean(errors.email)}
            helperText={touched.email && errors.email}
            autoComplete="email"
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={20} /> : null}
          >
            {isPending ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </form>

      <Box px={3} pb={2} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          Email us at{' '}
          <a
            href="mailto:support@quibbleai.io"
            style={{ color: '#1976d2', textDecoration: 'none' }}
          >
            support@quibbleai.io
          </a>
        </Typography>
      </Box>
    </Dialog>
  );
};

export default ForgotPasswordModal;
